import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import { timeAgo } from '@/lib/forum';

interface TopicRow {
  id: string;
  title: string;
  category: string;
  body: string | null;
  status: string;
  created_at: string;
}

function DashboardForum() {
  const { user } = useAuth();
  const [topics, setTopics] = useState<TopicRow[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('forum_topics')
        .select('*')
        .eq('owner_id', user.id)
        .order('created_at', { ascending: false });
      if (err) throw err;
      const rows = (data as TopicRow[]) || [];
      setTopics(rows);

      if (rows.length > 0) {
        const ids = rows.map((r) => r.id);
        const { data: replies, error: replyErr } = await supabase
          .from('forum_replies')
          .select('topic_id')
          .in('topic_id', ids);
        if (!replyErr && replies) {
          const map: Record<string, number> = {};
          (replies as { topic_id: string }[]).forEach((r) => {
            map[r.topic_id] = (map[r.topic_id] || 0) + 1;
          });
          setCounts(map);
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger vos discussions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    const { error: err } = await supabase.from('forum_topics').delete().eq('id', id);
    setDeletingId(null);
    if (err) {
      setError(err.message);
    } else {
      setTopics((prev) => prev.filter((t) => t.id !== id));
    }
  };

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50">
            Mes discussions
          </h1>
          <p className="text-sm text-foreground-400 mt-1">
            Gérez les discussions que vous avez publiées sur le forum.
          </p>
        </div>
        <Link
          to="/dashboard/forum/new"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-secondary-500 text-foreground-950 font-semibold rounded-lg hover:bg-secondary-400 transition-colors text-sm whitespace-nowrap"
        >
          <i className="ri-add-line text-lg" />
          Nouvelle discussion
        </Link>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400 mb-4">
          {error}
          <button onClick={load} className="ml-3 underline text-foreground-50">Réessayer</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-500/10">
            <i className="ri-loader-4-line text-2xl text-secondary-400 animate-spin" />
          </div>
        </div>
      ) : topics.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-secondary-500/10">
            <i className="ri-discuss-line text-3xl text-secondary-400" />
          </div>
          <h2 className="font-heading font-bold text-xl text-foreground-50 mb-2">
            Aucune discussion
          </h2>
          <p className="text-sm text-foreground-400 mb-6">
            Vous n&apos;avez pas encore lancé de discussion sur le forum.
          </p>
          <Link
            to="/dashboard/forum/new"
            className="inline-block px-6 py-2.5 bg-secondary-500 text-foreground-950 font-semibold rounded-lg hover:bg-secondary-400 transition-colors text-sm"
          >
            Lancer ma première discussion
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {topics.map((topic) => (
            <div
              key={topic.id}
              className="p-4 rounded-xl bg-background-100 border border-background-200/70"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary-500/10 text-secondary-400 whitespace-nowrap">
                      {topic.category}
                    </span>
                    <span className="text-xs text-foreground-400">
                      {timeAgo(topic.created_at.slice(0, 10))}
                    </span>
                  </div>
                  <Link to={`/forum/${topic.id}`}>
                    <h3 className="font-medium text-foreground-50 mt-1 hover:text-secondary-400 transition-colors">
                      {topic.title}
                    </h3>
                  </Link>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <Link
                    to={`/forum/${topic.id}`}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary-500/10 text-secondary-400 text-sm font-medium hover:bg-secondary-500/20 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-chat-3-line" />
                    {counts[topic.id] || 0} réponse{counts[topic.id] === 1 ? '' : 's'}
                  </Link>
                  <Link
                    to={`/dashboard/forum/${topic.id}/edit`}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-300/60 text-foreground-500 hover:text-foreground-50 hover:bg-background-200/60 transition-colors"
                    aria-label="Modifier"
                  >
                    <i className="ri-edit-line" />
                  </Link>
                  <button
                    type="button"
                    onClick={() => handleDelete(topic.id)}
                    disabled={deletingId === topic.id}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-300/60 text-foreground-500 hover:text-red-400 hover:bg-red-400/10 transition-colors disabled:opacity-50"
                    aria-label="Supprimer"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}

export default DashboardForum;